import { useState } from 'react';

export interface RespuestasEncuesta {
  /** Escala 1-5 por cada pregunta SUS adaptada */
  p1: number; // Usaría este sistema frecuentemente
  p2: number; // El sistema fue innecesariamente complejo
  p3: number; // El sistema fue fácil de usar
  p4: number; // Necesité ayuda técnica para usarlo
  p5: number; // Las funciones estaban bien integradas
  p6: number; // Hubo demasiada inconsistencia en el sistema
  p7: number; // La mayoría aprendería a usarlo rápidamente
  p8: number; // El sistema fue muy incómodo de usar
  p9: number; // Me sentí seguro usando el sistema
  p10: number; // Necesité aprender muchas cosas antes de usarlo
  comentario: string;
}

interface Props {
  onSubmit: (respuestas: RespuestasEncuesta) => void;
  onSkip: () => void;
  submitting?: boolean;
}

const PREGUNTAS = [
  { id: 'p1' as const, texto: 'Usaría este sistema de registro frecuentemente.' },
  { id: 'p2' as const, texto: 'El proceso de registro fue innecesariamente complejo.' },
  { id: 'p3' as const, texto: 'El formulario fue fácil de completar.' },
  { id: 'p4' as const, texto: 'Necesité ayuda de otra persona para completar el registro.' },
  { id: 'p5' as const, texto: 'Las secciones del formulario estaban bien organizadas.' },
  { id: 'p6' as const, texto: 'Encontré demasiadas inconsistencias en el formulario.' },
  { id: 'p7' as const, texto: 'Creo que la mayoría de personas aprendería a usarlo rápidamente.' },
  { id: 'p8' as const, texto: 'El formulario fue incómodo de usar.' },
  { id: 'p9' as const, texto: 'Me sentí seguro y cómodo completando el registro.' },
  { id: 'p10' as const, texto: 'Tuve que aprender muchas cosas antes de poder completar el formulario.' },
];

const ESCALA = [
  { valor: 1, label: 'Totalmente en desacuerdo' },
  { valor: 2, label: 'En desacuerdo' },
  { valor: 3, label: 'Neutral' },
  { valor: 4, label: 'De acuerdo' },
  { valor: 5, label: 'Totalmente de acuerdo' },
];

const initialRespuestas: RespuestasEncuesta = {
  p1: 0, p2: 0, p3: 0, p4: 0, p5: 0,
  p6: 0, p7: 0, p8: 0, p9: 0, p10: 0,
  comentario: '',
};

export default function EncuestaSatisfaccion({ onSubmit, onSkip, submitting }: Props) {
  const [respuestas, setRespuestas] = useState<RespuestasEncuesta>(initialRespuestas);
  const [errors, setErrors] = useState<Partial<Record<keyof RespuestasEncuesta, string>>>({});

  const handleSelect = (pregunta: keyof RespuestasEncuesta, valor: number) => {
    setRespuestas((prev) => ({ ...prev, [pregunta]: valor }));
    setErrors((prev) => ({ ...prev, [pregunta]: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof RespuestasEncuesta, string>> = {};
    PREGUNTAS.forEach(({ id }) => {
      if (!respuestas[id]) newErrors[id] = 'Por favor seleccione una opción';
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit(respuestas);
  };

  const respondidas = PREGUNTAS.filter(({ id }) => respuestas[id] > 0).length;
  const progreso = Math.round((respondidas / PREGUNTAS.length) * 100);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-[#0D3157] rounded-t-2xl px-6 py-5 sticky top-0 z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white/15 rounded-xl flex-shrink-0">
                <i className="ri-survey-line text-white text-xl" />
              </div>
              <div>
                <h2 className="text-white font-bold text-base leading-tight">
                  Encuesta de Satisfacción
                </h2>
                <p className="text-white/60 text-xs mt-0.5">
                  Basada en el estándar internacional SUS (System Usability Scale)
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onSkip}
              className="text-white/50 hover:text-white transition-colors cursor-pointer flex-shrink-0 mt-0.5"
              title="Omitir encuesta"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* Barra de progreso */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-white/60 text-xs">{respondidas} de {PREGUNTAS.length} preguntas respondidas</span>
              <span className="text-white/80 text-xs font-semibold">{progreso}%</span>
            </div>
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-400 rounded-full transition-all duration-300"
                style={{ width: `${progreso}%` }}
              />
            </div>
          </div>
        </div>

        {/* Cuerpo */}
        <div className="px-6 py-5">
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-600 text-base" />
            </div>
            <p className="text-amber-800 text-xs leading-relaxed">
              Su opinión es muy importante para mejorar el sistema. Esta encuesta es anónima y toma menos de 2 minutos. Indique su nivel de acuerdo con cada afirmación.
            </p>
          </div>

          {/* Leyenda de escala */}
          <div className="hidden md:flex items-center justify-between mb-3 px-1">
            <span className="text-xs text-slate-400 font-medium">Totalmente en desacuerdo</span>
            <span className="text-xs text-slate-400 font-medium">Totalmente de acuerdo</span>
          </div>

          <div className="space-y-4">
            {PREGUNTAS.map(({ id, texto }, idx) => (
              <div
                key={id}
                className={`rounded-xl border p-4 transition-colors ${
                  errors[id] ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'
                }`}
              >
                <p className="text-sm font-medium text-[#0D3157] mb-3 leading-snug">
                  <span className="text-slate-400 font-normal mr-1.5">{idx + 1}.</span>
                  {texto}
                </p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {ESCALA.map(({ valor, label }) => (
                    <button
                      key={valor}
                      type="button"
                      onClick={() => handleSelect(id, valor)}
                      title={label}
                      className={`flex-1 min-w-[44px] h-10 rounded-lg text-sm font-bold transition-all cursor-pointer border-2 ${
                        respuestas[id] === valor
                          ? 'bg-[#0D3157] border-[#0D3157] text-white scale-105'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-[#0D3157]/40 hover:text-[#0D3157]'
                      }`}
                    >
                      {valor}
                    </button>
                  ))}
                </div>
                {/* Etiquetas en mobile */}
                <div className="flex items-center justify-between mt-1.5 md:hidden">
                  <span className="text-xs text-slate-400">En desacuerdo</span>
                  <span className="text-xs text-slate-400">De acuerdo</span>
                </div>
                {errors[id] && (
                  <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                    <i className="ri-error-warning-line" />
                    {errors[id]}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Comentario adicional */}
          <div className="mt-5">
            <label className="block text-sm font-semibold text-[#0D3157] mb-2">
              Comentarios adicionales{' '}
              <span className="text-slate-400 font-normal text-xs">(opcional)</span>
            </label>
            <textarea
              value={respuestas.comentario}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  setRespuestas((prev) => ({ ...prev, comentario: e.target.value }));
                }
              }}
              placeholder="¿Qué mejoraría del formulario? ¿Tuvo alguna dificultad?"
              rows={3}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:border-[#0D3157]/50 resize-none"
            />
            <p className="text-xs text-slate-400 text-right mt-1">
              {respuestas.comentario.length}/500
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex flex-col sm:flex-row items-center gap-3">
          <button
            type="button"
            onClick={onSkip}
            className="w-full sm:w-auto flex items-center justify-center gap-2 border border-slate-300 text-slate-500 hover:text-slate-700 font-semibold text-sm px-5 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            Omitir encuesta
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full sm:flex-1 flex items-center justify-center gap-2 bg-[#0D3157] hover:bg-[#0a2444] text-white font-bold text-sm px-6 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Enviando…
              </>
            ) : (
              <>
                <i className="ri-send-plane-2-line" />
                Enviar encuesta y registrar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
